import { useSuiClientQuery } from '@mysten/dapp-kit';
import { Link } from 'react-router-dom';

import { useCpcStakePositions } from '~/hooks/useCpcStakePositions';
import { CPC_SYMBOL } from '~/utils/cpcCoin';
import { displayValidatorHeading } from '~/utils/displayValidator';
import { shortenAddress } from '~/utils/explorerFormat';

export function AddressStakePositions({ address }: { address: string }) {
	const { data: positions, isPending } = useCpcStakePositions(address);
	const { data: system } = useSuiClientQuery('getLatestSuiSystemState');
	const validators = system?.activeValidators ?? [];

	if (isPending) {
		return (
			<div className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-[#94a3b8]">
				正在读取质押…
			</div>
		);
	}
	if (!positions?.length) return null;

	return (
		<div className="rounded-xl border border-white/15 bg-white/10 p-4 text-[#e2e8f0]">
			<p className="text-xs font-medium uppercase tracking-wide text-[#94a3b8]">质押明细</p>
			<div className="mt-3 space-y-2">
				{positions.map((pos) => {
					const v = validators.find(
						(x) => x.suiAddress.toLowerCase() === pos.validatorAddress.toLowerCase(),
					);
					const name = v
						? displayValidatorHeading(v.name, v.description)
						: shortenAddress(pos.validatorAddress);
					const objectId = pos.fungibleStakedSuiId ?? pos.stakedSuiId;
					const kindLabel = pos.kind === 'fungible_staked_sui' ? '收益凭证' : '普通质押';
					const reward = pos.estimatedRewardCpc && Number(pos.estimatedRewardCpc) > 0
						? pos.estimatedRewardCpc
						: null;
					const showValue =
						pos.kind === 'fungible_staked_sui' &&
						pos.positionValueCpc &&
						pos.positionValueCpc !== pos.principalCpc;
					return (
						<div
							key={objectId ?? `${pos.validatorAddress}-${pos.kind}`}
							className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg border border-white/10 bg-black/25 px-3 py-2.5"
						>
							<div className="min-w-0">
								<p className="truncate text-sm font-semibold text-white">{name}</p>
								<p className="text-[11px] text-[#94a3b8]">
									{kindLabel}
									{pos.principalLocked
										? ` · 本金锁至 epoch ${pos.lockUntilEpoch}`
										: ' · 本金已解锁'}
									{reward ? ` · 当前可提 ${reward} ${CPC_SYMBOL}` : ''}
								</p>
							</div>
							<div className="text-right">
								<p className="text-[10px] text-[#94a3b8]">
									{pos.kind === 'fungible_staked_sui' ? '锁定本金' : '本金'}
								</p>
								<p className="tabular-nums text-base font-semibold text-white">
									{pos.principalCpc} {CPC_SYMBOL}
								</p>
								{showValue ? (
									<p className="text-[11px] tabular-nums text-[#94a3b8]">
										当前市值 {pos.positionValueCpc} {CPC_SYMBOL}
									</p>
								) : null}
								{objectId ? (
									<Link
										to={`/object/${objectId}`}
										className="font-mono text-[11px] text-[#38BDF8] hover:underline"
									>
										{shortenAddress(objectId)}
									</Link>
								) : null}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
